// Local actual: sale del slug de la URL (`/app/:locationSlug/...`, ARCHITECTURE §9). Cambiar de local cambia
// el slug y conserva la sección (UX §2.4).
import { useParams } from 'react-router';
import type { SessionLocation } from '@atte/contracts';
import { useQuery } from '@tanstack/react-query';
import { sessionQuery, useOptionalSession } from './session';

/** Local del slug de la URL, si está en el alcance del usuario. */
export function useOptionalLocation(): SessionLocation | undefined {
  const { locationSlug } = useParams();
  const { data } = useQuery({ ...sessionQuery, enabled: false });
  if (!locationSlug || !data) return undefined;
  return data.locations.find((l) => l.slug === locationSlug);
}

/** Local actual. Solo dentro de `/app/:locationSlug` (la guarda ya validó el slug). */
export function useCurrentLocation(): SessionLocation {
  const loc = useOptionalLocation();
  if (!loc) throw new Error('useCurrentLocation() fuera de /app/:locationSlug');
  return loc;
}

/** Id del local actual (para claves de consulta y rutas de la API). */
export function useLocationId(): string {
  return useCurrentLocation().id;
}

/** Ruta interna de una sección del local: appPath('centro', 'agenda', '2026-11-02'). */
export function appPath(slug: string, ...segments: string[]): string {
  const rest = segments.filter(Boolean).join('/');
  return `/app/${slug}${rest ? `/${rest}` : ''}`;
}

/** Hook: rutas relativas al local actual. */
export function useAppPath(): (...segments: string[]) => string {
  const { locationSlug } = useParams();
  return (...segments) => appPath(locationSlug ?? '', ...segments);
}

/**
 * Misma pantalla en otro local (UX §2.4): reemplaza el slug y conserva la sección; descarta la hoja abierta
 * y los filtros (los ids no sirven en otro local).
 */
export function switchLocationPath(pathname: string, nextSlug: string): string {
  const m = /^\/app\/[^/]+(\/[^/?#]*)?/.exec(pathname);
  const section = m?.[1] && m[1] !== '/' ? m[1] : '/agenda';
  return `/app/${nextSlug}${section}`;
}

/** Locales visibles: re-export para piezas que no están bajo /app. */
export function useLocations(): SessionLocation[] {
  return useOptionalSession()?.locations ?? [];
}
